<!-- 对话语言 -->
<template>
  <!-- 对话语言 -->
  <section
    ref="languagePanel"
    class="agent-service-language"
    :aria-label="$t('agentServiceUI.conversationLanguage')"
  >
    <button ref="languageTrigger" type="button" class="language-select"
      :class="{ 'is-open': pickerOpen, 'has-selection': Boolean(languageState.preference) }"
      aria-haspopup="listbox" :aria-expanded="pickerOpen"
      :title="languageTriggerLabel" :aria-label="languageTriggerLabel"
      :disabled="!isCustomer || !languageState.ready || languageState.changing"
      @click="openPicker">
      <svg v-if="iconOnly || !languageState.preference" class="language-globe" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.7" />
        <path d="M3 12h18M12 3a17 17 0 0 1 0 18 17 17 0 0 1 0-18Z"
          stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span v-else class="language-code" :lang="selectedLanguage" dir="auto">{{ selectedLanguageShortLabel }}</span>
    </button>
    <transition name="language-picker" @after-enter="focusSelectedOption" @after-leave="restoreTriggerFocus">
      <div v-if="pickerOpen" ref="languagePicker" class="agent-service-language-picker" :class="{ 'opens-right': placement === 'right' }" :style="popoverStyle">
        <!-- 对话语言列表 -->
        <div
          class="picker-list"
          role="listbox"
          :aria-label="$t('agentServiceUI.conversationLanguageList')"
          @keydown="navigateOptions"
        >
          <button
            v-for="option in options"
            :key="option.value"
            ref="languageOption"
            type="button"
            role="option"
            :aria-selected="selectedLanguage === option.value"
            :disabled="languageState.changing"
            :tabindex="selectedLanguage === option.value ? 0 : -1"
            @click="selectLanguage(option.value)"
          >
            <span class="option-name">
              <span :lang="option.value" dir="auto">{{
                option.nativeLabel
              }}</span>
              <span
                v-if="option.label !== option.nativeLabel"
                class="option-description"
                >{{ option.label }}</span
              >
            </span>
            <span
              v-if="selectedLanguage === option.value"
              class="option-check"
              aria-hidden="true"
              >✓</span
            >
          </button>
        </div>
        <p v-if="languageState.notice" class="picker-notice" role="status">
          {{ languageState.notice }}
        </p>
      </div>
    </transition>
    <div v-if="languageState.notice && !pickerOpen" ref="languageNotice" class="language-notice" :style="popoverStyle" role="status">
      <span>{{ languageState.notice }}</span>
      <!-- 重试 -->
      <button
        v-if="conversation && languageState.notice && !languageState.ready"
        type="button"
        @click="openLanguageSession"
      >
        {{ $t("agentServiceUI.retry") }}
      </button>
    </div>
  </section>
</template>

<script>
import { AgentServiceRealtimeClient } from "@/services/agent-service-realtime";
import { agentServiceLanguageApi } from "@/api/agentServiceLanguage";
import { createAgentServiceLanguageSession } from "@/services/agent-service-language-session";
import {
  readAgentServiceDefaultLanguage,
  AGENT_SERVICE_LANGUAGE_OPTIONS,
  agentServiceLanguageLabel,
  agentServiceLanguageShortLabel,
} from "@/services/agent-service-language";

// 页面会话期内按 conversationId 保留语言快照；后台复核时不先回退到默认语种。
const languageSnapshotCache = new Map();

/** 三个Agent窗口共享语种入口；只客户可改，未接通时不假装已保存。 */
export default {
  name: "AgentServiceLanguagePanel",
  props: {
    mobile: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    placement: { type: String, default: "below" },
    conversation: { type: Object, default: null },
    participantType: { type: String, default: "CUSTOMER" },
    messages: { type: Array, default: () => [] },
  },
  data() {
    return {
      languageState: {
        ready: false,
        changing: false,
        preference: null,
        translations: {},
        notice: "",
      },
      options: AGENT_SERVICE_LANGUAGE_OPTIONS,
      pickerOpen: false,
      returnFocusOnClose: true,
      popoverStyle: {},
    };
  },
  computed: {
    isCustomer() {
      return this.participantType === "CUSTOMER";
    },
    selectedLanguage() {
      return (
        this.languageState.preference?.customerLanguage ||
        readAgentServiceDefaultLanguage(
          process.env.VUE_APP_XSOU_DEFAULT_LANGUAGE
        )
      );
    },
    selectedLanguageLabel() {
      if (!this.languageState.preference)
        // 语言
        return this.$t("agentServiceUI.language");
      return agentServiceLanguageLabel(this.selectedLanguage);
    },
    selectedLanguageShortLabel() {
      return agentServiceLanguageShortLabel(this.selectedLanguage);
    },
    languageTriggerLabel() {
      return this.isCustomer
        ? `${this.$t("agentServiceUI.selectConversationLanguage")}：${this.selectedLanguageLabel}`
        : this.$t("agentServiceUI.customerLanguageWith", { language: this.selectedLanguageLabel });
    },
    sessionKey() {
      return this.active && this.conversation
        ? `${this.conversation.tenantId}:${this.conversation.conversationId}:${this.participantType}`
        : "";
    },
  },
  watch: {
    pickerOpen(open) {
      if (open) this.$nextTick(this.positionPopover);
    },
    "languageState.notice"() {
      this.$nextTick(this.positionPopover);
    },
    sessionKey() {
      this.pickerOpen = false;
      this.openLanguageSession();
    },
    messages() {
      this.languageSession?.refresh(this.messages);
    },
  },
  created() {
    this.languageSession = createAgentServiceLanguageSession({
      api: agentServiceLanguageApi,
      onChange: (state) => {
        const cached = this.sessionKey
          ? languageSnapshotCache.get(this.sessionKey)
          : null;
        const nextState =
          !state.preference && cached
            ? {
                ...state,
                preference: cached.preference,
                translations: cached.translations,
              }
            : state;
        this.languageState = nextState;
        if (this.sessionKey && nextState.preference) {
          languageSnapshotCache.set(this.sessionKey, {
            preference: nextState.preference,
            translations: nextState.translations,
          });
        }
        this.$emit("translations", nextState.translations);
        this.$emit("preference", nextState.preference);
      },
    });
    this.pollTimer = null;
    this.panelEpoch = 0;
  },
  mounted() {
    document.addEventListener("pointerdown", this.closePickerOutside, true);
    document.addEventListener("keydown", this.closePickerOnEscape, true);
    window.addEventListener("resize", this.positionPopover);
    window.visualViewport?.addEventListener("resize", this.positionPopover);
    window.visualViewport?.addEventListener("scroll", this.positionPopover);
    if (typeof ResizeObserver === 'function') {
      this.anchorObserver = new ResizeObserver(() => this.positionPopover());
      this.anchorObserver.observe(this.$refs.languagePanel);
      const header = this.$refs.languagePanel.closest('.workbench-tools, .contact-profile, .chat-header');
      const container = this.$refs.languagePanel.closest('.chat-container');
      if (container) this.anchorObserver.observe(container);
      if (header) this.anchorObserver.observe(header);
    }
    this.layoutAnchor = this.$refs.languagePanel.closest('.workbench-tools');
    this.layoutAnchor?.addEventListener('transitionend', this.positionPopover);
    this.openLanguageSession();
  },
  beforeDestroy() {
    this.anchorObserver?.disconnect();
    this.layoutAnchor?.removeEventListener('transitionend', this.positionPopover);
    document.removeEventListener("pointerdown", this.closePickerOutside, true);
    document.removeEventListener("keydown", this.closePickerOnEscape, true);
    window.removeEventListener("resize", this.positionPopover);
    window.visualViewport?.removeEventListener("resize", this.positionPopover);
    window.visualViewport?.removeEventListener("scroll", this.positionPopover);
    this.panelEpoch += 1;
    clearTimeout(this.pollTimer);
    this.languageRealtime?.close();
    this.languageSession.close();
  },
  methods: {
    /** 弹层优先与标题按钮组右沿对齐，再按可视区域收紧，窄窗口和拖动后的窗口不会把语言列表挤出屏幕。 */
    positionPopover() {
      const popover = this.$refs.languagePicker || this.$refs.languageNotice;
      const trigger = this.$refs.languageTrigger;
      const root = this.$refs.languagePanel;
      if (!popover || !trigger || !root || typeof window === "undefined") return;
      if (this.mobile) {
        const viewport = window.visualViewport;
        const bounds = trigger.closest?.('.chat-container')?.getBoundingClientRect();
        const rect = trigger.getBoundingClientRect(), origin = root.getBoundingClientRect();
        const leftEdge = Math.max((viewport?.offsetLeft || 0) + 12, (bounds?.left || 0) + 12);
        const rightEdge = Math.min((viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 12, (bounds?.right ?? window.innerWidth) - 12);
        const bottomEdge = Math.min((viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 12, (bounds?.bottom ?? window.innerHeight) - 12);
        const width = Math.max(0, Math.min(280, rightEdge - leftEdge));
        const left = Math.max(leftEdge, Math.min(rect.left, rightEdge - width));
        const top = Math.min(rect.bottom + 8, Math.max((bounds?.top || 0) + 8, bottomEdge - 120));
        this.popoverStyle = { left: `${left - origin.left}px`, right: 'auto', top: `${top - origin.top}px`, width: `${width}px`, maxHeight: `${Math.max(0, Math.min(360, bottomEdge - top))}px` };
        return;
      }
      if (this.placement === 'right') {
        const viewport = window.visualViewport;
        const bounds = (trigger.closest?.('.agent-service-window') || trigger.closest?.('.chat-container'))?.getBoundingClientRect();
        const triggerRect = trigger.getBoundingClientRect(), rootRect = root.getBoundingClientRect();
        const leftEdge = Math.max((viewport?.offsetLeft || 0) + 12, (bounds?.left ?? 0) + 12);
        const rightEdge = Math.min((viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 12, (bounds?.right ?? window.innerWidth) - 12);
        const bottomEdge = Math.min((viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 12, (bounds?.bottom ?? window.innerHeight) - 12);
        const available = rightEdge - triggerRect.right - 8;
        const width = Math.max(0, Math.min(280, available >= 160 ? available : rightEdge - leftEdge));
        const left = available >= 160 ? triggerRect.right + 8 : Math.max(leftEdge, Math.min(triggerRect.left, rightEdge - width));
        const top = available >= 160 ? triggerRect.top : triggerRect.bottom + 8;
        this.popoverStyle = { left: `${left - rootRect.left}px`, right: 'auto', top: `${top - rootRect.top}px`, width: `${width}px`, maxHeight: `${Math.max(0, Math.min(360, bottomEdge - top))}px` };
        return;
      }
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft || 0) + 16;
      const rightEdge = (viewport?.offsetLeft || 0) + (viewport?.width || window.innerWidth) - 16;
      const width = Math.min(280, Math.max(0, rightEdge - leftEdge));
      const anchor = trigger.closest?.(".header-actions") || trigger;
      const left = Math.max(leftEdge, Math.min(anchor.getBoundingClientRect().right - width, rightEdge - width));
      this.popoverStyle = { left: `${left - root.getBoundingClientRect().left}px`, right: "auto", width: `${width}px` };
    },
    closePickerOutside(event) {
      if (
        this.pickerOpen &&
        !this.languageState.changing &&
        !this.$refs.languagePanel?.contains(event.target)
      ) {
        // 点击输入区或其他控件时不把焦点抢回语言选择框。
        this.returnFocusOnClose = false;
        this.pickerOpen = false;
      }
    },
    closePickerOnEscape(event) {
      if (
        this.pickerOpen &&
        event.key === "Escape" &&
        !this.languageState.changing
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.returnFocusOnClose = true;
        this.pickerOpen = false;
      }
    },
    /** 同一选择框点击展开或合上；保存中禁止切换，动画完成后才定位当前选项。 */
    openPicker() {
      if (
        this.isCustomer &&
        this.languageState.ready &&
        !this.languageState.changing
      ) {
        this.returnFocusOnClose = true;
        this.pickerOpen = !this.pickerOpen;
      }
    },
    focusSelectedOption() {
      if (!this.pickerOpen) return;
      const index = Math.max(
        0,
        this.options.findIndex((item) => item.value === this.selectedLanguage)
      );
      this.$refs.languageOption?.[index]?.focus();
    },
    restoreTriggerFocus() {
      if (this.active && !this.pickerOpen && this.returnFocusOnClose) this.$refs.languageTrigger?.focus();
    },
    /** 列表支持方向键、首尾跳转与Esc，不依赖鼠标或悬停。 */
    navigateOptions(event) {
      if (this.languageState.changing) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.returnFocusOnClose = true;
        this.pickerOpen = false;
        return;
      }
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const items = this.$refs.languageOption || [];
      const current = items.indexOf(event.target);
      const next =
        event.key === "Home"
          ? 0
          : event.key === "End"
          ? items.length - 1
          : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) %
            items.length;
      items[next]?.focus();
    },
    async selectLanguage(language) {
      if (!this.isCustomer || this.languageState.changing) return;
      await this.changeLanguage(language);
      // 只在权威回执确认成功后关闭；失败保留列表与当前偏好。
      if (this.selectedLanguage === language && !this.languageState.notice)
        this.pickerOpen = false;
    },
    acceptNotification(notification) {
      if (!this.active) return;
      return this.languageSession.acceptNotification(
        notification,
        this.messages
      );
    },
    async openLanguageSession() {
      const epoch = ++this.panelEpoch;
      clearTimeout(this.pollTimer);
      this.languageRealtime?.close(); this.languageRealtime = null;
      this.languageSession.close();
      if (!this.sessionKey) return;
      await this.languageSession.open({
        conversationId: this.conversation.conversationId,
        tenantId: this.conversation.tenantId,
        participantType: this.participantType,
      });
      if (epoch !== this.panelEpoch || !this.languageState.ready) return;
      const current = () => epoch === this.panelEpoch && this.active;
      this.languageRealtime = new AgentServiceRealtimeClient({
        token: String(this.$store.state.user?.userInfo?.token || ""),
        conversationId: this.conversation.conversationId,
        onLanguageNotification: notification => current() ? this.acceptNotification(notification) : null,
        onRefresh: () => current() ? this.languageSession.refresh(this.messages) : Promise.resolve(),
      });
      this.languageRealtime.connect();
    },
    changeLanguage(language) {
      return this.languageSession.change(language);
    },
  },
};
</script>

<style lang="less" scoped>
.agent-service-language {
  position: relative;
  z-index: 3;
  display: inline-flex;
  flex: 0 0 44px;
  align-items: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--m-text-secondary, #667085);
  font-size: 13px;
  line-height: 20px;
  touch-action: manipulation;

  .language-select {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    transition: color 160ms ease;

    .language-globe { width: 22px; height: 22px; }
    .language-code {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
    }
    &.is-open,
    &.has-selection { color: var(--m-brand-600, #246de0); }
    &:focus-visible { outline: 2px solid #4286f5; outline-offset: -2px; border-radius: 8px; }
    &:disabled { cursor: default; }
    @media (prefers-reduced-motion: reduce) { transition: none; }
  }

  .language-notice {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    width: min(280px, calc(100vw - 32px));
    box-sizing: border-box;
    padding: 8px 12px;
    border: 1px solid var(--m-border-default, #dde3ee);
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, .12);
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
    button {
      min-width: 44px;
      min-height: 44px;
      margin-left: 4px;
      padding: 0 8px;
      border: 0;
      background: transparent;
      color: #246de0;
      font: inherit;
      cursor: pointer;
    }
  }
}
.agent-service-language-picker {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: min(280px, calc(100vw - 32px));
  max-height: min(360px, calc(100dvh - 208px));
  box-sizing: border-box;
  border: 1px solid var(--m-divider, #eef2f7);
  border-radius: var(--chat-radius, 12px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--m-surface, #fff);
  color: var(--m-text-primary, #334155);
  button {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    &:focus-visible {
      outline: 2px solid var(--m-brand-500, #4286f5);
      outline-offset: -2px;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }
  .picker-list {
    touch-action: pan-y;
    overflow-y: auto;
    overscroll-behavior: contain;
    min-height: 0;
    padding: 8px 12px;
    button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      text-align: start;
      width: 100%;
      min-height: 44px;
      padding: 8px 12px;
      border-radius: var(--chat-radius, 12px);
      font-size: 14px;
      line-height: 22px;
      &[aria-selected="true"] {
        color: var(--m-brand-600, #246de0);
        background: var(--m-brand-50, #f2f7ff);
      }
      .option-name {
        display: flex;
        flex-direction: column;
      }
      .option-description {
        color: var(--m-text-secondary, #667085);
        font-size: 12px;
        line-height: 18px;
      }
      .option-check {
        flex: 0 0 auto;
        margin-left: 12px;
      }
    }
  }
  .picker-notice {
    margin: 8px 16px;
    font-size: 13px;
    line-height: 20px;
  }
  @media (max-height: 400px) {
    .picker-list {
      padding: 4px 12px;
    }
  }
  &.language-picker-enter-active {
    transition: opacity 240ms cubic-bezier(.2,.8,.2,1), transform 240ms cubic-bezier(.2,.8,.2,1), clip-path 240ms cubic-bezier(.2,.8,.2,1);
  }
  &.language-picker-leave-active {
    transition: opacity 240ms cubic-bezier(.2,.8,.2,1), transform 240ms cubic-bezier(.2,.8,.2,1), clip-path 240ms cubic-bezier(.2,.8,.2,1);
  }
  &.language-picker-enter,
  &.language-picker-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }
  &.opens-right {
    transform-origin: left top;
    clip-path: inset(0 round 12px);
    &.language-picker-enter, &.language-picker-leave-to { transform: translateX(-8px); clip-path: inset(0 100% 0 0 round 12px); }
  }
  @media (prefers-reduced-motion: reduce) {
    &.language-picker-enter-active,
    &.language-picker-leave-active { transition: none; }
  }
}
</style>
